import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  Modal,
  Typography,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { tokens, withAlpha } from "../../Common/designTokens";

const MODAL_BOX_SX = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 420,
  bgcolor: tokens.colors.bodyBackgroundColor,
  border: `1px solid ${tokens.colors.bodyBorderColor50}`,
  boxShadow: 24,
  p: 4,
  borderRadius: "8px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
};

const TITLE_SX = {
  color: tokens.colors.fontColour,
  mb: 2,
  mt: 0,
  textAlign: "center",
  fontWeight: "bold",
  fontSize: "1.25rem",
};

const BODY_SX = {
  color: tokens.colors.fontColour85,
  textAlign: "center",
  lineHeight: 1.45,
};

const SECONDARY_BUTTON_SX = {
  textTransform: "none",
  fontWeight: 700,
  minWidth: 120,
  whiteSpace: "nowrap",
  px: 2,
  borderRadius: "8px",
  backgroundColor: tokens.colors.defaultGridRowColor,
  "&:hover": {
    borderColor: tokens.palette.primary,
    backgroundColor: tokens.colors.defaultGridHoverColor,
  },
};

const PRIMARY_BUTTON_SX = {
  textTransform: "none",
  fontWeight: 700,
  minWidth: 140,
  whiteSpace: "nowrap",
  px: 2,
  borderRadius: "8px",
  backgroundColor: tokens.palette.primary,
  color: tokens.colors.white,
  "&:hover": {
    backgroundColor: tokens.palette.primaryA80,
  },
  "&.Mui-disabled": {
    backgroundColor: tokens.colors.disabledBackground,
    color: tokens.colors.disabledFontColour,
  },
};

const DANGER_BUTTON_SX = {
  ...PRIMARY_BUTTON_SX,
  backgroundColor: tokens.palette.danger,
  "&:hover": {
    backgroundColor: withAlpha(tokens.palette.danger, 0.8),
  },
};

const INLINE_ACTION_ROW_SX = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  gap: 1.5,
  flexWrap: "wrap",
  width: "100%",
};

const MAWatchlistConfirmModals = ({
  isDeleteAllConfirmOpen,
  onDismissDeleteAllConfirmation,
  clearingWatchlist,
  onConfirmDeleteAll,
  isCloseConfirmOpen,
  onDismissCloseConfirmation,
  saving,
  onConfirmClose,
  isDynamicToStaticConfirmOpen,
  onDismissDynamicToStaticConfirmation,
  onConfirmDynamicToStaticConfirmation,
  isDynamicSaveCriteriaWarningOpen,
  isDynamicNoCriteriaErrorOpen,
  hasAppliedDynamicCriteria,
  onDismissDynamicSaveCriteriaWarning,
  onApplyAndReviewDynamicCriteria,
  onContinueDynamicSaveWithAppliedCriteria,
  onDismissDynamicNoCriteriaError,
}) => (
  <>
    <Modal
      open={isDeleteAllConfirmOpen}
      onClose={onDismissDeleteAllConfirmation}
      aria-labelledby="watchlist-delete-all-confirmation-title"
    >
      <Box sx={MODAL_BOX_SX}>
        <Typography
          id="watchlist-delete-all-confirmation-title"
          variant="h6"
          component="h2"
          sx={TITLE_SX}
        >
          Delete All
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
          <WarningAmberIcon sx={{ fontSize: 48, color: tokens.palette.danger }} />
        </Box>
        <Typography sx={{ ...BODY_SX, mb: 3 }}>
          Are you sure you want to delete all bonds from this watchlist? This action can't be undone.
        </Typography>
        <Box sx={INLINE_ACTION_ROW_SX}>
          <Button
            variant="outlined"
            onClick={onDismissDeleteAllConfirmation}
            disabled={clearingWatchlist}
            sx={SECONDARY_BUTTON_SX}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={onConfirmDeleteAll}
            disabled={clearingWatchlist}
            startIcon={clearingWatchlist ? <CircularProgress size={20} color="inherit" /> : null}
            sx={DANGER_BUTTON_SX}
          >
            Delete
          </Button>
        </Box>
      </Box>
    </Modal>

    <Modal
      open={isCloseConfirmOpen}
      onClose={onDismissCloseConfirmation}
      aria-labelledby="watchlist-close-confirmation-title"
    >
      <Box sx={MODAL_BOX_SX}>
        <Typography
          id="watchlist-close-confirmation-title"
          variant="h6"
          component="h2"
          sx={TITLE_SX}
        >
          Warning
        </Typography>
        <Typography sx={{ ...BODY_SX, mb: 3 }}>
          Unsaved changes will be lost. Are you sure you want to close without saving?
        </Typography>
        <Box sx={INLINE_ACTION_ROW_SX}>
          <Button
            variant="outlined"
            onClick={onDismissCloseConfirmation}
            disabled={saving}
            sx={SECONDARY_BUTTON_SX}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={onConfirmClose}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : null}
            sx={PRIMARY_BUTTON_SX}
          >
            Close
          </Button>
        </Box>
      </Box>
    </Modal>

    <Modal
      open={isDynamicToStaticConfirmOpen}
      onClose={onDismissDynamicToStaticConfirmation}
      aria-labelledby="watchlist-dynamic-to-static-confirmation-title"
    >
      <Box sx={{ ...MODAL_BOX_SX, width: 520 }}>
        <Typography
          id="watchlist-dynamic-to-static-confirmation-title"
          variant="h6"
          component="h2"
          sx={TITLE_SX}
        >
          Switch to Static Watchlist?
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
          <WarningAmberIcon sx={{ fontSize: 48, color: tokens.palette.danger }} />
        </Box>
        <Typography sx={{ ...BODY_SX, mb: 3 }}>
          This watchlist is currently driven by dynamic characteristics. If you switch to Static and save changes, existing watchlist items will be cleared and rebuilt from your manual selections. Do you want to continue?
        </Typography>
        <Box sx={INLINE_ACTION_ROW_SX}>
          <Button
            variant="outlined"
            onClick={onDismissDynamicToStaticConfirmation}
            disabled={saving || clearingWatchlist}
            sx={SECONDARY_BUTTON_SX}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={onConfirmDynamicToStaticConfirmation}
            disabled={saving || clearingWatchlist}
            sx={PRIMARY_BUTTON_SX}
          >
            Continue
          </Button>
        </Box>
      </Box>
    </Modal>

    <Modal
      open={isDynamicSaveCriteriaWarningOpen}
      onClose={onDismissDynamicSaveCriteriaWarning}
      aria-labelledby="dynamic-watchlist-unapplied-characteristics-title"
    >
      <Box sx={{ ...MODAL_BOX_SX, width: 500 }}>
        <Typography
          id="dynamic-watchlist-unapplied-characteristics-title"
          variant="h6"
          component="h2"
          sx={TITLE_SX}
        >
          Unapplied Characteristics
        </Typography>
        <Typography sx={{ ...BODY_SX, mb: 2.5, maxWidth: 440 }}>
          {hasAppliedDynamicCriteria
            ? "The current characteristics have not been applied to the preview. Apply them to review matching bonds, or continue with the last applied characteristics."
            : "The selected characteristics have not been applied yet. Please apply them first before saving."}
        </Typography>
        {!hasAppliedDynamicCriteria ? (
          <Typography
            sx={{
              color: tokens.palette.danger,
              mb: 3.5,
              textAlign: "center",
              fontSize: "0.96rem",
              fontWeight: 700,
            }}
          >
            No characteristics have been applied in<br />Dynamic mode yet.
          </Typography>
        ) : null}
        <Box sx={INLINE_ACTION_ROW_SX}>
          {hasAppliedDynamicCriteria ? (
            <>
              <Button
                variant="outlined"
                onClick={onDismissDynamicSaveCriteriaWarning}
                disabled={saving}
                sx={SECONDARY_BUTTON_SX}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={onContinueDynamicSaveWithAppliedCriteria}
                disabled={saving}
                sx={{ ...PRIMARY_BUTTON_SX, minWidth: 160 }}
              >
                Continue Anyway
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              onClick={onDismissDynamicSaveCriteriaWarning}
              disabled={saving}
              sx={PRIMARY_BUTTON_SX}
            >
              OK
            </Button>
          )}
        </Box>
      </Box>
    </Modal>

    <Modal
      open={isDynamicNoCriteriaErrorOpen}
      onClose={onDismissDynamicNoCriteriaError}
      aria-labelledby="dynamic-watchlist-no-characteristics-title"
    >
      <Box sx={{ ...MODAL_BOX_SX, width: 500 }}>
        <Typography
          id="dynamic-watchlist-no-characteristics-title"
          variant="h6"
          component="h2"
          sx={TITLE_SX}
        >
          Attention
        </Typography>
        <Typography sx={{ ...BODY_SX, mb: 3, maxWidth: 440 }}>
          Please select characteristics to create a dynamic watchlist.
        </Typography>
        <Box sx={INLINE_ACTION_ROW_SX}>
          <Button
            variant="contained"
            onClick={onDismissDynamicNoCriteriaError}
            disabled={saving}
            sx={PRIMARY_BUTTON_SX}
          >
            OK
          </Button>
        </Box>
      </Box>
    </Modal>
  </>
);

export default MAWatchlistConfirmModals;
